import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  downloadInspectionDocument,
  getInspectionChecklistQueries,
  getInspectionFindings,
  getInspectionGeneratedReports,
  getInspectionList,
  getInspectionPlanList,
  getLoggedInUser,
  getUsersList,
  previewInspectionDocument,
} from "../../helpers/backend_helper";
import {
  asList,
  csvCell,
  dateLabel,
  dateValue,
  isOverdue,
  normalizeRecord,
} from "./inspectionWorkspaceData";
import "./InspectionWorkspaceIndex.scss";

const PAGE_SIZE = 10;
const details = {
  queries: {
    title: "Queries",
    description:
      "Track and respond to questions raised during inspection work.",
    icon: "bx-message-rounded-dots",
  },
  findings: {
    title: "Findings",
    description: "Review and manage findings identified during inspections.",
    icon: "bx-flag",
  },
  reports: {
    title: "Inspection Reports",
    description: "View and manage reports generated from inspection reviews.",
    icon: "bx-bar-chart-alt-2",
  },
};

const currentPeriod = (date, period) => {
  const value = dateValue(date);
  if (!value) return false;
  const now = new Date(),
    then = new Date(value);
  if (period === "month")
    return (
      then.getFullYear() === now.getFullYear() &&
      then.getMonth() === now.getMonth()
    );
  if (period === "quarter")
    return (
      then.getFullYear() === now.getFullYear() &&
      Math.floor(then.getMonth() / 3) === Math.floor(now.getMonth() / 3)
    );
  return then.getFullYear() === now.getFullYear();
};

const unique = (rows, field) =>
  [
    ...new Set(
      rows.map((row) => row[field]).filter((value) => value && value !== "—")
    ),
  ].sort();

const badgeTone = (value) =>
  /high|overdue/i.test(value)
    ? "danger"
    : /medium|progress|review/i.test(value)
    ? "warning"
    : /resolved|closed|completed|low/i.test(value)
    ? "success"
    : "blue";

const reviewPath = (id) => `/inspectionreview/${encodeURIComponent(id)}`;

export default function InspectionWorkspaceIndex({ type }) {
  const navigate = useNavigate();
  const config = details[type];
  const [rows, setRows] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [revision, setRevision] = useState(0);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState({});
  const [filters, setFilters] = useState({});
  const [quick, setQuick] = useState("");
  const [card, setCard] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setWarning("");
    (async () => {
      try {
        const reviewList = asList(await getInspectionList());
        const [usersResult, plansResult, account] = await Promise.all([
          type === "findings" || type === "queries"
            ? getUsersList().catch(() => null)
            : null,
          type === "reports" ? getInspectionPlanList().catch(() => null) : null,
          getLoggedInUser().catch(() => null),
        ]);
        const users = usersResult?.status ? usersResult.data || [] : [];
        const plans = plansResult?.status ? plansResult.data || [] : [];
        const fetcher =
          type === "queries"
            ? getInspectionChecklistQueries
            : type === "findings"
            ? getInspectionFindings
            : getInspectionGeneratedReports;
        const loaded = [];
        let failed = 0;
        for (let index = 0; index < reviewList.length; index += 6) {
          const batch = await Promise.all(
            reviewList.slice(index, index + 6).map(async (review) => {
              try {
                return asList(await fetcher(review.id)).map((item) =>
                  normalizeRecord(type, item, review, users, plans)
                );
              } catch {
                failed += 1;
                return [];
              }
            })
          );
          loaded.push(...batch.flat());
        }
        if (active) {
          setRows(loaded);
          setCurrentUser(account);
          if (failed)
            setWarning(
              `${failed} inspection${
                failed === 1 ? "" : "s"
              } could not be loaded. Refresh to try again.`
            );
        }
      } catch (reason) {
        if (active)
          setError(
            reason.message || `Unable to load ${config.title.toLowerCase()}.`
          );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [type, revision, config.title]);

  const stats = useMemo(() => {
    if (type === "queries")
      return [
        ["All", rows.length, "bx-message-rounded-dots", "blue"],
        [
          "Open",
          rows.filter((r) => r.status === "Open").length,
          "bx-time-five",
          "danger",
        ],
        [
          "In Progress",
          rows.filter((r) => r.status === "In Progress").length,
          "bx-loader-circle",
          "warning",
        ],
        [
          "Resolved",
          rows.filter((r) => r.status === "Resolved").length,
          "bx-check-circle",
          "success",
        ],
        ["Overdue", rows.filter(isOverdue).length, "bx-error", "danger"],
      ];
    if (type === "findings")
      return [
        ["Total Findings", rows.length, "bx-flag", "blue"],
        ...["High", "Medium", "Low"].map((severity, index) => [
          `${severity} Severity`,
          rows.filter(
            (r) => r.severity?.toLowerCase() === severity.toLowerCase()
          ).length,
          ["bx-error-circle", "bx-error", "bx-check-circle"][index],
          ["danger", "warning", "success"][index],
        ]),
        [
          "Open Findings",
          rows.filter(
            (r) => !["Resolved", "Closed", "Withdrawn"].includes(r.status)
          ).length,
          "bx-time-five",
          "purple",
        ],
        [
          "Closed Findings",
          rows.filter((r) => ["Resolved", "Closed"].includes(r.status)).length,
          "bx-flag",
          "blue",
        ],
      ];
    return [
      ["Total Reports", rows.length, "bx-file", "blue"],
      [
        "Completed",
        rows.filter((r) => r.status === "Completed").length,
        "bx-check-circle",
        "success",
      ],
      [
        "Under Review",
        rows.filter((r) => r.status === "Under Review").length,
        "bx-user-voice",
        "purple",
      ],
      [
        "Draft",
        rows.filter((r) => r.status === "Draft").length,
        "bx-time-five",
        "warning",
      ],
      [
        "This Month",
        rows.filter((r) => currentPeriod(r.createdOn, "month")).length,
        "bx-calendar",
        "blue",
      ],
    ];
  }, [rows, type]);

  const filtered = useMemo(() => {
    const text = search.trim().toLowerCase();
    const userId = currentUser?.id ?? currentUser?.userId;
    const result = rows.filter((row) => {
      if (
        text &&
        ![
          row.id,
          row.title,
          row.description,
          row.engagement,
          row.inspection,
          row.client,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(text)
        )
      )
        return false;
      if (filters.status && row.status !== filters.status) return false;
      if (filters.priority && row.priority !== filters.priority) return false;
      if (filters.severity && row.severity !== filters.severity) return false;
      if (filters.engagement && row.engagement !== filters.engagement)
        return false;
      if (filters.inspection && row.inspection !== filters.inspection)
        return false;
      if (filters.assignee && row.assignee !== filters.assignee) return false;
      if (filters.reportType && row.reportType !== filters.reportType)
        return false;
      if (filters.cycle && row.cycle !== filters.cycle) return false;
      if (filters.client && row.client !== filters.client) return false;
      if (
        filters.year &&
        new Date(row.createdOn).getFullYear().toString() !== filters.year
      )
        return false;
      if (
        card &&
        (type === "queries"
          ? card === "Overdue"
            ? !isOverdue(row)
            : card !== "All" && row.status !== card
          : type === "findings"
          ? card.includes("Severity")
            ? row.severity?.toLowerCase() !== card.split(" ")[0].toLowerCase()
            : card === "Open Findings"
            ? ["Resolved", "Closed", "Withdrawn"].includes(row.status)
            : card === "Closed Findings"
            ? !["Resolved", "Closed"].includes(row.status)
            : false
          : card === "This Month"
          ? !currentPeriod(row.createdOn, "month")
          : ["Completed", "Under Review", "Draft"].includes(card) &&
            row.status !== card)
      )
        return false;
      if (quick === "mine" && Number(row.createdBy) !== Number(userId))
        return false;
      if (quick === "assigned" && Number(row.assigneeId) !== Number(userId))
        return false;
      if (quick === "high" && row.priority !== "High") return false;
      if (quick === "overdue" && !isOverdue(row)) return false;
      if (
        quick === "week" &&
        !(
          dateValue(row.dueDate) >= new Date().setHours(0, 0, 0, 0) &&
          dateValue(row.dueDate) < Date.now() + 7 * 86400000
        )
      )
        return false;
      if (quick === "completed" && row.status !== "Completed") return false;
      if (
        ["month", "quarter", "year"].includes(quick) &&
        !currentPeriod(row.createdOn, quick)
      )
        return false;
      return true;
    });
    return result.sort((a, b) =>
      sort === "oldest"
        ? dateValue(a.updatedOn || a.createdOn) -
          dateValue(b.updatedOn || b.createdOn)
        : sort === "title"
        ? a.title.localeCompare(b.title)
        : dateValue(b.updatedOn || b.createdOn) -
          dateValue(a.updatedOn || a.createdOn)
    );
  }, [rows, search, filters, quick, card, sort, type, currentUser]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice(
    (Math.min(page, pageCount) - 1) * PAGE_SIZE,
    Math.min(page, pageCount) * PAGE_SIZE
  );

  const changeFilter = (name, value) =>
    setDraft((current) => ({ ...current, [name]: value }));

  const resetPage = () => {
    setPage(1);
    setSelected([]);
  };

  const options = (label, field, all) => (
    <label key={field}>
      <span>{label}</span>
      <select
        value={draft[field] || ""}
        onChange={(event) => changeFilter(field, event.target.value)}
      >
        <option value="">{all}</option>
        {unique(rows, field).map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </select>
    </label>
  );

  const exportRows = () => {
    const columns =
      type === "reports"
        ? [
            ["ID", "id"],
            ["Report Title", "title"],
            ["Report Type", "reportType"],
            ["Inspection Cycle", "cycle"],
            ["Engagement", "engagement"],
            ["Inspection", "inspection"],
            ["Client", "client"],
            ["Generated Date", "createdOn"],
          ]
        : [
            ["ID", "id"],
            ["Finding Title", "title"],
            ["Engagement", "engagement"],
            ["Inspection", "inspection"],
            ["Severity", "severity"],
            ["Status", "status"],
            ["Assigned To", "assignee"],
            ["Identified Date", "createdOn"],
            ["Target Date", "dueDate"],
          ];
    const chosen = selected.length
      ? filtered.filter((row) => selected.includes(row.key))
      : filtered;
    const csv = [
      columns.map(([label]) => csvCell(label)).join(","),
      ...chosen.map((row) =>
        columns
          .map(([, field]) =>
            csvCell(
              field.endsWith("On") || field === "dueDate"
                ? dateLabel(row[field])
                : row[field]
            )
          )
          .join(",")
      ),
    ].join("\r\n");
    const url = URL.createObjectURL(
      new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `${type}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const openReport = async (row, download) => {
    setBusy(row.key);
    setError("");
    try {
      if (download)
        await downloadInspectionDocument(
          row.reviewId,
          row.documentId,
          row.fileName
        );
      else await previewInspectionDocument(row.reviewId, row.documentId);
    } catch (reason) {
      setError(reason.message || "Unable to open the report.");
    } finally {
      setBusy("");
    }
  };
  const fields =
    type === "queries"
      ? [
          ["Status", "status", "All Status"],
          ["Priority", "priority", "All Priorities"],
          ["Engagement", "engagement", "All Engagements"],
          ["Assigned To", "assignee", "All Users"],
        ]
      : type === "findings"
      ? [
          ["Status", "status", "All Status"],
          ["Severity", "severity", "All Severities"],
          ["Engagement", "engagement", "All Engagements"],
          ["Inspection", "inspection", "All Inspections"],
          ["Assigned To", "assignee", "All Users"],
        ]
      : [
          ["Report Type", "reportType", "All Types"],
          ["Inspection Cycle", "cycle", "All Cycles"],
          ["Engagement", "engagement", "All Engagements"],
          ["Client", "client", "All Clients"],
          ["Year", "year", "All Years"],
        ];
  const quickFilters =
    type === "queries"
      ? [
          ["mine", "My Queries", "bx-user"],
          ["assigned", "Assigned to Me", "bx-user-pin"],
          ["high", "High Priority", "bx-flag"],
          ["overdue", "Overdue", "bx-error"],
          ["week", "Due This Week", "bx-calendar"],
        ]
      : [
          ["completed", "Completed Reports", "bx-check-circle"],
          ["month", "This Month", "bx-calendar"],
          ["quarter", "This Quarter", "bx-calendar"],
          ["year", "This Year", "bx-calendar"],
        ];
  return (
    <main
      className={(`page-content inspection-workspace inspection-workspace--${type}`) + " piers-page"}
    >
      <header className="inspection-workspace__header piers-page-header">
        <div>
          <h1 className="piers-page-title">{config.title}</h1>
          <p>{config.description}</p>
        </div>
        <div className="inspection-workspace__header-actions">
          <button
            type="button"
            className="inspection-workspace__refresh piers-refresh-button"
            onClick={() => setRevision((value) => value + 1)}
            disabled={loading}
          >
            <i className="bx bx-refresh" aria-hidden="true" /> Refresh
          </button>
          {type !== "queries" && (
            <button
              type="button"
              className="inspection-workspace__outline"
              onClick={exportRows}
              disabled={!filtered.length}
            >
              <i className="bx bx-download" /> Export
            </button>
          )}
        </div>
      </header>
      {error && (
        <div role="alert" className="inspection-workspace__alert">
          {error}{" "}
          <button onClick={() => setRevision((value) => value + 1)}>
            Retry
          </button>
        </div>
      )}
      {warning && (
        <div role="alert" className="inspection-workspace__alert">
          {warning}{" "}
          <button className="piers-refresh-button" onClick={() => setRevision((value) => value + 1)}>
            Refresh
          </button>
        </div>
      )}
      <section
        className="inspection-workspace__stats"
        aria-label={`${config.title} summary`}
      >
        {stats.map(([label, count, icon, tone]) => (
          <button
            key={label}
            type="button"
            className={`inspection-workspace__stat ${
              card === label || (!card && label === stats[0][0])
                ? "is-active"
                : ""
            }`}
            onClick={() => {
              setCard(label === stats[0][0] ? "" : card === label ? "" : label);
              resetPage();
            }}
          >
            <span className={`inspection-workspace__stat-icon is-${tone}`}>
              <i className={`bx ${icon}`} />
            </span>
            <span>
              <strong>{label}</strong>
              <b>{count}</b>
              {type !== "queries" && (
                <small>
                  {label === "Total Findings"
                    ? "All findings"
                    : label === "Total Reports"
                    ? "Generated reports"
                    : label === "This Month"
                    ? "Reports generated"
                    : label === "High Severity"
                    ? "Needs immediate attention"
                    : label === "Medium Severity"
                    ? "Monitor and address"
                    : label === "Low Severity"
                    ? "Minor issues"
                    : label === "Open Findings"
                    ? "Not yet closed"
                    : label === "Closed Findings"
                    ? "Resolved / Accepted"
                    : label === "Completed"
                    ? "100% of total"
                    : ""}
                </small>
              )}
            </span>
          </button>
        ))}
      </section>
      {type !== "findings" && (
        <section className="inspection-workspace__quick">
          <strong>Quick Filters:</strong>
          {quickFilters.map(([id, label, icon]) => (
            <button
              key={id}
              type="button"
              className={quick === id ? "is-active" : ""}
              onClick={() => {
                setQuick(quick === id ? "" : id);
                resetPage();
              }}
            >
              <i className={`bx ${icon}`} />
              {label}
            </button>
          ))}
          {type === "reports" && (
            <button
              type="button"
              className="inspection-workspace__clear"
              onClick={() => {
                setQuick("");
                setCard("");
                resetPage();
              }}
            >
              Clear All
            </button>
          )}
        </section>
      )}
      <section
        className="inspection-workspace__filters"
        aria-label="Search and filter"
      >
        <label className="inspection-workspace__search">
          <i className="bx bx-search" />
          <input
            aria-label={`Search ${config.title.toLowerCase()}`}
            placeholder={`Search ${config.title.toLowerCase()} by title, description, engagement...`}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              resetPage();
            }}
          />
        </label>
        {fields.map(([label, field, all]) =>
          field === "year" ? (
            <label key="year">
              <span>Year</span>
              <select
                value={draft.year || ""}
                onChange={(event) => changeFilter("year", event.target.value)}
              >
                <option value="">All Years</option>
                {[
                  ...new Set(
                    rows
                      .map((row) => new Date(row.createdOn).getFullYear())
                      .filter(Number.isFinite)
                  ),
                ]
                  .sort((a, b) => b - a)
                  .map((year) => (
                    <option key={year}>{year}</option>
                  ))}
              </select>
            </label>
          ) : (
            options(label, field, all)
          )
        )}
        <button
          type="button"
          className="inspection-workspace__clear"
          onClick={() => {
            setDraft({});
            setFilters({});
            setSearch("");
            setCard("");
            setQuick("");
            resetPage();
          }}
        >
          Clear
        </button>
        <button
          type="button"
          className="inspection-workspace__primary"
          onClick={() => {
            setFilters(draft);
            resetPage();
          }}
        >
          Apply
        </button>
      </section>
      <section className="inspection-workspace__results">
        <header>
          <h2>
            {config.title} ({filtered.length})
          </h2>
          <div>
            <span>Sort by</span>
            <select
              aria-label="Sort results"
              value={sort}
              onChange={(event) => {
                setSort(event.target.value);
                resetPage();
              }}
            >
              <option value="newest">
                {type === "reports"
                  ? "Generated"
                  : type === "findings"
                  ? "Identified"
                  : "Updated"}{" "}
                Date (Newest)
              </option>
              <option value="oldest">Date (Oldest)</option>
              <option value="title">Title (A–Z)</option>
            </select>
            {type !== "queries" && (
              <div className="inspection-workspace__view">
                <button
                  type="button"
                  title="List view"
                  aria-pressed={view === "list"}
                  className={view === "list" ? "is-active" : ""}
                  onClick={() => setView("list")}
                >
                  <i className="bx bx-list-ul" />
                </button>
                <button
                  type="button"
                  title="Grid view"
                  aria-pressed={view === "grid"}
                  className={view === "grid" ? "is-active" : ""}
                  onClick={() => setView("grid")}
                >
                  <i className="bx bx-grid-alt" />
                </button>
              </div>
            )}
          </div>
        </header>
        {loading ? (
          <div className="inspection-workspace__empty" role="status">
            Loading {config.title.toLowerCase()}…
          </div>
        ) : !filtered.length ? (
          <div className="inspection-workspace__empty">
            <i className={`bx ${config.icon}`} />
            <h3>No {config.title.toLowerCase()} found</h3>
            <p>
              {rows.length
                ? "Try changing the search or filters."
                : type === "reports"
                ? "Reports will appear here after inspection reviews are submitted and approved."
                : `Open an inspection review to ${
                    type === "queries" ? "raise a query" : "record a finding"
                  }.`}
            </p>
          </div>
        ) : view === "grid" && type !== "queries" ? (
          <div className="inspection-workspace__grid">
            {visible.map((row) => (
              <article key={row.key}>
                <div className="inspection-workspace__grid-top">
                  <span
                    className={`inspection-workspace__badge is-${badgeTone(
                      row.severity || row.status
                    )}`}
                  >
                    {row.severity || row.status}
                  </span>
                  <span>{row.id}</span>
                </div>
                <button
                  type="button"
                  className="inspection-workspace__row-link"
                  onClick={() => navigate(reviewPath(row.reviewId))}
                >
                  {row.title}
                </button>
                <p>
                  {row.engagement} / {row.inspection}
                </p>
                <small>
                  {type === "reports" ? "Generated" : "Identified"}:{" "}
                  {dateLabel(row.createdOn)}
                </small>
                {type === "reports" && (
                  <div className="inspection-workspace__actions">
                    <button
                      onClick={() => openReport(row, false)}
                      disabled={busy === row.key}
                    >
                      Preview
                    </button>
                    <button
                      onClick={() => openReport(row, true)}
                      disabled={busy === row.key}
                    >
                      Download
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="inspection-workspace__table-wrap">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Select all on this page"
                      checked={
                        visible.length > 0 &&
                        visible.every((row) => selected.includes(row.key))
                      }
                      onChange={(event) =>
                        setSelected((current) =>
                          event.target.checked
                            ? [
                                ...new Set([
                                  ...current,
                                  ...visible.map((row) => row.key),
                                ]),
                              ]
                            : current.filter(
                                (key) => !visible.some((row) => row.key === key)
                              )
                        )
                      }
                    />
                  </th>
                  {(type === "queries"
                    ? [
                        "ID",
                        "Query Title",
                        "Engagement / Inspection",
                        "Priority",
                        "Status",
                        "Assigned To",
                        "Due Date",
                        "Updated",
                      ]
                    : type === "findings"
                    ? [
                        "ID",
                        "Finding Title",
                        "Engagement / Inspection",
                        "Severity",
                        "Status",
                        "Assigned To",
                        "Identified Date",
                        "Target Date",
                        "Last Updated",
                        "",
                      ]
                    : [
                        "ID",
                        "Report Title",
                        "Report Type",
                        "Inspection Cycle",
                        "Engagement",
                        "Inspection",
                        "Client",
                        "Generated Date",
                        "Actions",
                      ]
                  ).map((heading, index) => (
                    <th key={`${heading}-${index}`}>{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.id}`}
                        checked={selected.includes(row.key)}
                        onChange={(event) =>
                          setSelected((current) =>
                            event.target.checked
                              ? [...current, row.key]
                              : current.filter((key) => key !== row.key)
                          )
                        }
                      />
                    </td>
                    <td>{row.id}</td>
                    <td>
                      <button
                        type="button"
                        className="inspection-workspace__row-link"
                        onClick={() => navigate(reviewPath(row.reviewId))}
                      >
                        {row.title}
                      </button>
                    </td>
                    {type === "reports" ? (
                      <>
                        <td>{row.reportType}</td>
                        <td>{row.cycle}</td>
                        <td>{row.engagementNo}</td>
                        <td>{row.inspection}</td>
                        <td>{row.client}</td>
                        <td>{dateLabel(row.createdOn)}</td>
                        <td>
                          <div className="inspection-workspace__actions">
                            <button
                              title="Preview"
                              aria-label={`Preview ${row.title}`}
                              onClick={() => openReport(row, false)}
                              disabled={busy === row.key}
                            >
                              <i className="bx bx-show" />
                            </button>
                            <button
                              title="Download"
                              aria-label={`Download ${row.title}`}
                              onClick={() => openReport(row, true)}
                              disabled={busy === row.key}
                            >
                              <i className="bx bx-download" />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>
                          {row.engagement} / {row.inspection}
                        </td>
                        <td>
                          <span
                            className={`inspection-workspace__badge is-${badgeTone(
                              row.priority || row.severity
                            )}`}
                          >
                            {row.priority || row.severity}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`inspection-workspace__badge is-${badgeTone(
                              row.status
                            )}`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td>{row.assignee}</td>
                        {type === "findings" && (
                          <td>{dateLabel(row.createdOn)}</td>
                        )}
                        <td className={isOverdue(row) ? "is-overdue" : ""}>
                          {dateLabel(row.dueDate)}
                        </td>
                        <td>{dateLabel(row.updatedOn)}</td>
                        {type === "findings" && (
                          <td>
                            <button
                              className="inspection-workspace__dots"
                              title="Open finding in review"
                              onClick={() => navigate(reviewPath(row.reviewId))}
                            >
                              <i className="bx bx-dots-vertical-rounded" />
                            </button>
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <footer>
          <span>
            Showing{" "}
            {filtered.length
              ? (Math.min(page, pageCount) - 1) * PAGE_SIZE + 1
              : 0}{" "}
            to{" "}
            {Math.min(Math.min(page, pageCount) * PAGE_SIZE, filtered.length)}{" "}
            of {filtered.length} {config.title.toLowerCase()}
          </span>
          <nav aria-label="Pagination">
            <button
              disabled={page <= 1}
              onClick={() => setPage((value) => value - 1)}
              aria-label="Previous page"
            >
              <i className="bx bx-chevron-left" />
            </button>
            {Array.from({ length: pageCount }, (_, index) => (
              <button
                key={index}
                className={page === index + 1 ? "is-active" : ""}
                onClick={() => setPage(index + 1)}
              >
                {index + 1}
              </button>
            ))}
            <button
              disabled={page >= pageCount}
              onClick={() => setPage((value) => value + 1)}
              aria-label="Next page"
            >
              <i className="bx bx-chevron-right" />
            </button>
          </nav>
        </footer>
      </section>
    </main>
  );
}
